import { useEffect, useState } from 'react';
import { getCustomerPortalAccess } from '@rechargeapps/storefront-client';
import { refreshRechargeSession, canRefreshSession } from '../utils/sessionRefresh';

// A member submitting fresh credentials on these can race the refresh below.
const AUTH_PATHS = ['/login', '/forgot-password', '/admin/access', '/magiclink/redirect'];
const isAuthPage = () => AUTH_PATHS.some((p) => window.location.pathname.startsWith(p));

/**
 * Hook that periodically validates the Recharge session token stored in localStorage.
 * Returns a boolean flag `isExpired` indicating whether the session is no longer valid.
 *
 * @param {Object}   options
 * @param {number}   options.interval  How often (ms) to check the token. Defaults to 45 seconds.
 */
const useSessionValidator = ({ interval = 45_000 } = {}) => {
  const [isExpired, setIsExpired] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [hasValidated, setHasValidated] = useState(false);

  useEffect(() => {
    let timerId;

    const validateSession = async () => {
      if (isAuthPage()) {
        setIsValidating(false);
        setHasValidated(true);
        return;
      }

      // Mark the beginning of a validation cycle
      setIsValidating(true);

      try {
        const stored = localStorage.getItem('rechargeSession');
        let session = stored ? JSON.parse(stored) : null;

        // A missing or malformed session isn't the end of the road if we still
        // hold a Shopify token to mint a new one from.
        const isUsableSession = (s) => Boolean(s?.apiToken && s?.customerId);
        if (!isUsableSession(session) && canRefreshSession()) {
          session = await refreshRechargeSession();
        }

        if (!isUsableSession(session)) {
          setIsExpired(true);
          return;
        }

        // This call resolves successfully (2xx) when the session is still valid.
        await getCustomerPortalAccess(session, { page_destination: 'overview' });
        // If we reach here without throwing, the session is valid.
        setIsExpired(false);
      } catch (err) {
        // A 401 (unauthorized) or similar error means the token is no longer valid.
        if (err?.status === 401 || err?.message?.includes('401')) {
          setIsExpired(true);
        }
      } finally {
        // Mark the end of a validation cycle
        setIsValidating(false);
        setHasValidated(true);
      }
    };

    // Perform an immediate check on mount.
    validateSession();

    // Schedule subsequent checks.
    timerId = setInterval(validateSession, interval);

    return () => clearInterval(timerId);
  }, [interval]);

  return { isExpired, isValidating, hasValidated };
};

export default useSessionValidator; 